import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from '../../i18n';
import { saveDocumentBody, saveDocumentTitle, locateDocument } from '../../extensions/bundled/documents/documentsStore';
import { docBodyCache as docCache, docBodyCacheKey as docCacheKey } from '../../extensions/bundled/documents/docBodyCache';
import { updateDocumentTabLabel, updateDocumentTabScope, setDocumentTabUnsaved } from '../../store/tabsStore';
import { followDocumentTab } from '../../store/documentTabWorkspaces';
import { bumpDocumentsTree } from '../../extensions/bundled/documents/documentsUiStore';
import { registerAiDocEditor } from '../../services/aiExtensionBridges';
import { sanitizeRichTextHtml } from '../../utils/sanitizeHtml';
import { stableStringify } from '../ui/lexicalBlocks';
import { changedSince, createBaseline, movedSince, noteRemote, noteSaved } from './blockBaseline';

export const SAVE_DEBOUNCE_MS = 600;
// The docbody row's HTML snapshot (for older app versions — documentsStore
// saveDocumentBody) is refreshed once typing has settled this long, and when
// the tab closes; autosaves in between send only the changed blocks.
export const HTML_SNAPSHOT_IDLE_MS = 20000;
// …and at least this often while someone keeps typing, so an older app
// version opening the page meanwhile is never far behind.
export const HTML_SNAPSHOT_MAX_AGE_MS = 60000;

// How many of this client's own recent save payloads (Lexical blocks,
// stableStringify'd — see lexicalBlocks.js) are kept to recognize an inbound
// record as our own echo.
const OWN_SAVE_ECHO_RING_SIZE = 4;

// The session cache of opened documents lives in
// extensions/bundled/documents/docBodyCache.js (imported above as
// docCache/docCacheKey — a mechanical extraction, same Map + key scheme) so
// the upload outbox's closed-doc rewrite can invalidate an entry after
// patching a stored body this tab is not currently showing.

/**
 * One open document's load / autosave session (moved out of
 * DocumentEditorTab.js, documents redesign 13.10.2026 — a pure move).
 *
 * Title and body autosave on a short debounce — title to the document's tree
 * record (relabeling the tab + sidebar), body to its own `docbody` record
 * (dual-written as HTML + Lexical blocks — see documentsStore.saveDocumentBody).
 * Content is sanitized on save.
 *
 * Returns the state the view renders plus the refs the collab/reconcile half
 * (useDocumentCollab.js) shares with it: both halves read and write the SAME
 * refs, which is why they travel together as one `session` object.
 */
/** True for the empty-title tab label in the current or the fallback language. */
const isUntitledLabel = (label) =>
  label === i18n.t('docSidebar.untitled') || label === i18n.t('docSidebar.untitled', { lng: 'en' });

export function useDocumentBody({ documentId, scope, tabLabel }) {
  const cacheKey = docCacheKey(documentId, scope);
  const cached = docCache.get(cacheKey);
  const [status, setStatus] = useState(cached ? 'ready' : 'loading'); // 'loading' | 'ready' | 'notfound'
  const [title, setTitle] = useState(cached?.title ?? '');
  const [initialBody, setInitialBody] = useState(cached?.bodyHtml ?? '');
  // Non-null only while the editor should import Lexical blocks on mount; a
  // legacy-HTML remount (AI apply, or a legacy remote update while unfocused)
  // clears this back to null so that remount falls through to the HTML `value`
  // path instead of re-importing now-stale blocks (LexicalRichEditor reads
  // `valueBlocks` once, on mount, per instance).
  const [initialBlocks, setInitialBlocks] = useState(cached?.bodyLexical ?? null);
  // Bumped when the AI assistant (or a legacy-HTML remote reconcile) applies
  // content, forcing the Lexical editor to remount with the new body/blocks
  // (both `value` and `valueBlocks` are only read on mount).
  const [editorNonce, setEditorNonce] = useState(0);
  // Body-change listeners (the toolbar's word count, the outline rail). A
  // listener set, not state: a state bump per keystroke would re-render the
  // whole tab — and the editor with it — on every character.
  const bodyListenersRef = useRef(new Set());
  const notifyBody = useCallback((local = false) => {
    bodyListenersRef.current.forEach((fn) => {
      try {
        fn(bodyHtmlRef.current || '', { local });
      } catch (err) {
        console.error('[documents] body listener failed:', err);
      }
    });
  }, []);
  const subscribeBody = useCallback((fn) => {
    bodyListenersRef.current.add(fn);
    return () => bodyListenersRef.current.delete(fn);
  }, []);

  const editorRef = useRef(null);
  // Latest values + debounce timers live in refs so a flush-on-unmount can read
  // them without stale closures.
  const bodyHtmlRef = useRef(cached?.bodyHtml ?? '');
  const bodyBlocksRef = useRef(cached?.bodyLexical ?? null);
  const titleRef = useRef(cached?.title ?? '');
  const titleTimer = useRef(null);
  const bodyTimer = useRef(null);
  // True while a saveDocumentBody() call for this document is in flight (i.e.
  // issued but not yet resolved). Together with `bodyTimer.current` (a save
  // still debouncing) this tells the docbody reconcile effect whether our own
  // local body state has moved ahead of whatever the server has acked so far.
  const bodySaveInFlightRef = useRef(false);
  // True whenever a change reaching onChange/onBlocksChange originated from
  // applyRemoteOps/applyFullBlocks (see collab.onRemoteApplied) rather than
  // the local user typing — gates the save-scheduling so a remote echo never
  // re-triggers our own debounced save. The microtask reset is a safety net
  // for the case where the apply turns out to be a no-op (fully
  // caret-protected) and onRemoteApplied is therefore never called.
  const applyingRemoteRef = useRef(false);
  const applyRemote = useCallback((fn) => {
    applyingRemoteRef.current = true;
    fn();
    Promise.resolve().then(() => {
      applyingRemoteRef.current = false;
    });
  }, []);
  // Ring buffer of stableStringify(bodyLexical) for the last few saves THIS
  // client has issued (pushed the moment each save payload is built). An
  // inbound docbody record whose bodyLexical matches one of these is
  // unambiguously our own echo (not a teammate's edit).
  const savedLexicalRingRef = useRef([]);
  const recordOwnSave = useCallback((snapshot) => {
    if (!snapshot) return;
    const ring = savedLexicalRingRef.current;
    ring.push(stableStringify(snapshot));
    if (ring.length > OWN_SAVE_ECHO_RING_SIZE) ring.shift();
  }, []);
  // Bumped on every local editor change that schedules a save, and whenever
  // an inbound collab:block_ops batch is applied — read by the reconcile
  // recency gate (useDocumentCollab.js).
  const lastLocalEditAtRef = useRef(0);
  const lastOpsAppliedAtRef = useRef(0);
  // Tracks whether the editor's contenteditable currently has DOM focus — used
  // only to decide whether a legacy (HTML-only) remote update may repaint the
  // editor immediately or must wait for blur.
  const editorFocusedRef = useRef(false);

  // Which blocks the person here changed (blockBaseline.js): a save writes
  // only those.
  const baselineRef = useRef(createBaseline());
  const setBaseline = useCallback((snapshot) => {
    baselineRef.current = createBaseline(snapshot);
  }, []);
  /** A teammate's edit was applied (`ids`: the blocks it replaced): those are theirs, not ours. */
  // Set by the record reconcile right before it applies: those blocks come
  // with their stored places (blockBaseline.noteRemote `place`).
  const placeNextRemoteRef = useRef(false);
  const noteRemoteBlocks = useCallback((prev, next, ids = null) => {
    noteRemote(baselineRef.current, next, ids, prev, { place: placeNextRemoteRef.current });
    placeNextRemoteRef.current = false;
  }, []);
  const snapshotTimer = useRef(null);
  const lastSnapshotAtRef = useRef(Date.now());

  const syncCache = useCallback(() => {
    docCache.set(cacheKey, {
      title: titleRef.current,
      bodyHtml: bodyHtmlRef.current,
      bodyLexical: bodyBlocksRef.current,
    });
  }, [cacheKey]);

  useEffect(() => {
    let cancelled = false;
    locateDocument(documentId, scope)
      .then(({ doc, scope: foundScope }) => {
        if (cancelled) return;
        // Moved between the cloud and this device since the tab/link was made:
        // follow it, so saves go to where it lives now.
        if (doc && foundScope.type !== scope.type) {
          followDocumentTab(documentId, foundScope, updateDocumentTabScope);
          return;
        }
        if (!doc) {
          if (!docCache.has(cacheKey)) setStatus('notfound');
          return;
        }
        titleRef.current = doc.title || '';
        bodyHtmlRef.current = doc.bodyHtml || '';
        bodyBlocksRef.current = doc.bodyLexical || null;
        // The baseline is what the EDITOR shows: an editor mounted from the
        // session cache shows the cached body, which may be newer than the
        // store (teammates' live edits not saved yet) or older (their saves
        // since). Either way, what differs from the store was not changed
        // here, so it is not written from here; replacing the editor with the
        // stored copy instead threw away live text (e2e D6, 25.10.2026).
        setBaseline((cached && cached.bodyLexical) || doc.bodyLexical || { blocks: [] });
        setTitle(doc.title || '');
        setInitialBody(doc.bodyHtml || '');
        setInitialBlocks(doc.bodyLexical || null);
        syncCache();
        setStatus('ready');
        notifyBody();
      })
      .catch(() => {
        if (!cancelled && !docCache.has(cacheKey)) setStatus('notfound');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, cacheKey, syncCache]);

  // Reflect an external rename (from the sidebar) into the title input. The
  // sidebar rename always writes a non-empty name, and clearing the title only
  // ever happens via this input, so syncing only real names avoids ever wiping
  // the field. The (translated) "Untitled" is the placeholder label for an
  // empty title — ignore it, in either language.
  useEffect(() => {
    if (status !== 'ready' || !tabLabel || isUntitledLabel(tabLabel)) return;
    if (tabLabel !== titleRef.current) {
      titleRef.current = tabLabel;
      setTitle(tabLabel);
      syncCache();
    }
  }, [tabLabel, status, syncCache]);

  const flushTitle = useCallback(async () => {
    if (titleTimer.current) {
      clearTimeout(titleTimer.current);
      titleTimer.current = null;
    }
    await saveDocumentTitle(documentId, titleRef.current, scope);
    bumpDocumentsTree();
    if (!bodyTimer.current) setDocumentTabUnsaved(documentId, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, scope.type, scope.teamId]);

  const saveBodyNow = useCallback(async ({ htmlSnapshot = false } = {}) => {
    bodySaveInFlightRef.current = true;
    const blocks = bodyBlocksRef.current;
    recordOwnSave(blocks);
    // The blocks changed here since the baseline (null: not known, save all).
    const changed = changedSince(baselineRef.current, blocks);
    const moved = changed ? movedSince(baselineRef.current, blocks) : null;
    try {
      const result = await saveDocumentBody(
        documentId,
        { bodyHtml: sanitizeRichTextHtml(bodyHtmlRef.current || ''), bodyLexical: blocks },
        scope,
        { htmlSnapshot, onlyIds: changed ? new Set(changed.keys()) : null, movedIds: moved }
      );
      baselineRef.current = noteSaved(baselineRef.current, changed, blocks);
      // The head (the whole copy for older app versions) was just written —
      // by this snapshot, or by moving the body to blocks.
      if (htmlSnapshot || result?.head) lastSnapshotAtRef.current = Date.now();
    } finally {
      bodySaveInFlightRef.current = false;
    }
    if (!titleTimer.current) setDocumentTabUnsaved(documentId, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, scope.type, scope.teamId]);

  const flushBody = useCallback(async () => {
    if (bodyTimer.current) {
      clearTimeout(bodyTimer.current);
      bodyTimer.current = null;
    }
    if (snapshotTimer.current) {
      clearTimeout(snapshotTimer.current);
      snapshotTimer.current = null;
    }
    await saveBodyNow({ htmlSnapshot: true });
  }, [saveBodyNow]);

  // Flush any pending save when the doc changes or the tab unmounts (with the
  // HTML snapshot, when one is still owed).
  useEffect(() => {
    return () => {
      if (titleTimer.current) flushTitle();
      if (bodyTimer.current || snapshotTimer.current) flushBody();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  // AI assistant live-edit bridge: write_document drives the open editor through
  // this so the user sees the change and autosave persists it. Registered once
  // the document is ready (bodyHtmlRef populated). The AI writes plain HTML, so
  // this always takes the legacy remount path — bodyLexical is explicitly
  // cleared on save (not preserved-stale) since it no longer describes the new
  // HTML; the next real local edit dual-writes fresh blocks derived from it.
  useEffect(() => {
    if (status !== 'ready') return undefined;
    const api = {
      getBody: () => bodyHtmlRef.current || '',
      applyBody: (html) => {
        const next = sanitizeRichTextHtml(html || '');
        bodyHtmlRef.current = next;
        bodyBlocksRef.current = null;
        setInitialBody(next);
        setInitialBlocks(null);
        setEditorNonce((n) => n + 1);
        syncCache();
        setDocumentTabUnsaved(documentId, true);
        if (bodyTimer.current) clearTimeout(bodyTimer.current);
        // The whole body is replaced: every block is a candidate on the next save.
        setBaseline(null);
        bodyTimer.current = setTimeout(async () => {
          bodyTimer.current = null;
          // The editor is remounted with this HTML and derives the seeded ids
          // itself: the store must hold the same ones (no id adoption).
          await saveDocumentBody(documentId, { bodyHtml: sanitizeRichTextHtml(bodyHtmlRef.current || ''), bodyLexical: null }, scope, { adoptIds: false });
          if (!titleTimer.current) setDocumentTabUnsaved(documentId, false);
        }, SAVE_DEBOUNCE_MS);
      },
    };
    return registerAiDocEditor(documentId, api);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, status, syncCache, scope.type, scope.teamId]);

  const onTitleChange = (e) => {
    const next = e.target.value;
    setTitle(next);
    titleRef.current = next;
    syncCache();
    updateDocumentTabLabel(documentId, next.trim() || i18n.t('docSidebar.untitled'));
    setDocumentTabUnsaved(documentId, true);
    if (titleTimer.current) clearTimeout(titleTimer.current);
    titleTimer.current = setTimeout(async () => {
      titleTimer.current = null;
      await saveDocumentTitle(documentId, titleRef.current, scope);
      bumpDocumentsTree();
      if (!bodyTimer.current) setDocumentTabUnsaved(documentId, false);
    }, SAVE_DEBOUNCE_MS);
  };

  // Single combined debounce for the body: both onBodyChange (html) and
  // onBodyBlocksChange (Lexical blocks) fire in the same synchronous tick for
  // any settled edit (see LexicalRichEditor's handleChange), so either one
  // (re)arming the same timer is enough — the timer reads whatever both refs
  // hold when it fires, dual-writing bodyHtml + bodyLexical together.
  const scheduleBodySave = useCallback(() => {
    setDocumentTabUnsaved(documentId, true);
    if (bodyTimer.current) clearTimeout(bodyTimer.current);
    bodyTimer.current = setTimeout(() => {
      bodyTimer.current = null;
      saveBodyNow({ htmlSnapshot: Date.now() - lastSnapshotAtRef.current > HTML_SNAPSHOT_MAX_AGE_MS });
    }, SAVE_DEBOUNCE_MS);
    if (snapshotTimer.current) clearTimeout(snapshotTimer.current);
    snapshotTimer.current = setTimeout(() => {
      snapshotTimer.current = null;
      if (!bodyTimer.current) saveBodyNow({ htmlSnapshot: true });
    }, HTML_SNAPSHOT_IDLE_MS);
  }, [documentId, saveBodyNow]);

  const onBodyChange = (html) => {
    bodyHtmlRef.current = html;
    syncCache();
    notifyBody(!applyingRemoteRef.current);
    if (applyingRemoteRef.current) return; // remote echo — onRemoteApplied owns the baseline, no save
    lastLocalEditAtRef.current = Date.now();
    scheduleBodySave();
  };

  const onBodyBlocksChange = (snapshot) => {
    bodyBlocksRef.current = snapshot;
    syncCache();
    if (applyingRemoteRef.current) return; // remote echo — see onBodyChange
    lastLocalEditAtRef.current = Date.now();
    scheduleBodySave();
  };

  /** The document as the exports and Print read it right now. */
  const snapshot = useCallback(
    () => ({
      title: titleRef.current || i18n.t('docSidebar.untitled'),
      bodyHtml: bodyHtmlRef.current || '',
      bodyLexical: bodyBlocksRef.current || null,
    }),
    []
  );

  return {
    status,
    title,
    initialBody,
    initialBlocks,
    editorNonce,
    subscribeBody,
    notifyBody,
    setInitialBody,
    setInitialBlocks,
    setEditorNonce,
    editorRef,
    bodyHtmlRef,
    bodyBlocksRef,
    titleRef,
    bodyTimer,
    bodySaveInFlightRef,
    applyingRemoteRef,
    applyRemote,
    savedLexicalRingRef,
    noteRemoteBlocks,
    placeNextRemoteRef,
    lastLocalEditAtRef,
    lastOpsAppliedAtRef,
    editorFocusedRef,
    syncCache,
    onTitleChange,
    onBodyChange,
    onBodyBlocksChange,
    flushTitle,
    flushBody,
    snapshot,
  };
}
